import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { GridHeaderWithOverflow } from '../../../components/grid-header/grid-header-with-overflow';
import { PlanFeaturesTrialUpsellBanner } from '../../../components/plan-features-trial-upsell-banner';
import { BannerSources } from '../../../components/plan-features-trial-upsell-banner/constants';
import { PagingContextProvider } from '../../../contexts/paging-context';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useEnablePurchaseOrders } from '../../../feature-flags/hooks/use-enable-purchase-orders';
import { useShouldDisplayAutoRoutingControls } from '../../../hooks/auto-routing/use-should-display-auto-routing-controls';
import { useIsAutoSplitEnabledForSeller } from '../../../hooks/use-is-auto-splitting-enabled-for-seller';
import { useUserSetting } from '../../../hooks/use-user-setting';
import { useIsHarmonizationSuggestionEnabled } from '../../../products/harmonization/use-is-harmonization-suggestion-enabled';
import { PAGE_SIZE_250 } from '../../../services/pagination-service';
import { setUseAggregateInventory } from '../../../store/products/products.actions';
import { getUseConsolidatedInventory } from '../../../store/products/products.api';
import { FlexRow, Gap } from '../../../ui-components/components/flex';
import style from '../../grid-page.module.less';
import messages from '../messages';
import { actionButtonsForProductGrid } from './action-buttons';
import { ActiveShippingLocationsButton } from './action-buttons/active-shipping-locations-button';
import { AutoSplitButton } from './action-buttons/autosplit-button';
import { CreatePurchaseOrderButton } from './action-buttons/create-purchase-order-button';
import EditColumnsButtonContainer from './action-buttons/edit-columns-button';
import { LastUpdatedTag } from './components/last-updated-tag';
import ProductsFilterBar from './filter-bar';
import ProductGrid from './grid';
import ImportLogModal from './modals/import-log/import-log-modal';
import ProductContextProvider from './product-context';
import { ProductPagingControlWrapper } from './product-paging-control-wrapper';
import productsStyle from './style.module.less';

const actionButtons = [
  actionButtonsForProductGrid.addPresetGroupButton,
  actionButtonsForProductGrid.categoryButton,
  actionButtonsForProductGrid.activeShippingLocationsButton,
  actionButtonsForProductGrid.productsTagButton,
  actionButtonsForProductGrid.importButton,
  actionButtonsForProductGrid.exportButton,
  actionButtonsForProductGrid.deactivateButton,
  actionButtonsForProductGrid.deleteButton,
  actionButtonsForProductGrid.newProductButton,
  actionButtonsForProductGrid.combineButton,
  actionButtonsForProductGrid.autoSplitButton,
  actionButtonsForProductGrid.createPurchaseOrderButton,
  actionButtonsForProductGrid.bulkHarmonizationButton,
];

const ProductsPage = () => {
  const dispatch = useDispatch();
  const { importLogModalOpen } = useSelector((state) => state.products);
  const isPurchaseOrdersEnabled = useEnablePurchaseOrders();
  const userSetting = useUserSetting(UserSettings.productGridPageSize.settingName);
  const userPageSize = useMemo(() => parseInt(userSetting || PAGE_SIZE_250, 10), [userSetting]);
  const [shouldUseAggregateInventory, setShouldUseAggregateInventory] = useState(false);
  const isSmartRoutingEnabled = useShouldDisplayAutoRoutingControls();
  const isAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const isHarmonizationSuggestionEnabled = useIsHarmonizationSuggestionEnabled();

  const filteredActionButtons = useMemo(
    () =>
      actionButtons.filter((button) => {
        return (
          (button.type !== ActiveShippingLocationsButton || isSmartRoutingEnabled) &&
          (button.type !== AutoSplitButton || isAutoSplitEnabled) &&
          (button.type !== CreatePurchaseOrderButton || isPurchaseOrdersEnabled) &&
          (button.type !== actionButtonsForProductGrid.bulkHarmonizationButton.type || isHarmonizationSuggestionEnabled)
        );
      }),
    [isSmartRoutingEnabled, isAutoSplitEnabled, isPurchaseOrdersEnabled, isHarmonizationSuggestionEnabled],
  );

  useEffect(() => {
    const consolidatedInvPromise = getUseConsolidatedInventory();
    consolidatedInvPromise.promise
      .then((data) => {
        setShouldUseAggregateInventory(data);
        dispatch(setUseAggregateInventory(data));
      })
      .catch(() => {
        setShouldUseAggregateInventory(false);
        dispatch(setUseAggregateInventory(false));
      });
    return () => consolidatedInvPromise?.cancel();
  }, [dispatch]);

  const view = useMemo(
    () => ({
      id: 'productGridHeader',
      buttons: filteredActionButtons.map((button, index) => <button.type key={`${index}`} id={index} overflowName={button.overflowName} />),
      label: (
        <FlexRow className={productsStyle['products-header-container']} gap={Gap.Space12}>
          <FormattedMessage {...messages.products} />
          <LastUpdatedTag />
        </FlexRow>
      ),
    }),
    [filteredActionButtons],
  );

  return (
    <div className={style['main-content']}>
      {importLogModalOpen ? <ImportLogModal /> : null}
      <PagingContextProvider page={1} pageSize={userPageSize}>
        <ProductContextProvider shouldUseAggregateInventory={shouldUseAggregateInventory}>
          <div className={style['grid-header']}>
            <GridHeaderWithOverflow
              bannerSlot={
                <PlanFeaturesTrialUpsellBanner
                  className={productsStyle['plan-features-trial-upsell-banner']}
                  source={BannerSources.Products}
                />
              }
              ignoreCustomOverflow
              view={view}
            >
              <EditColumnsButtonContainer />
            </GridHeaderWithOverflow>
          </div>
          <div className={classnames(style['edit-grid-wrap'], style['edit-grid-wrap-no-filters'])}>
            <div className={style['grid-filter-wrap']}>
              <ProductsFilterBar />
            </div>
          </div>
          <div className={style['grid-content']}>
            <div className={classnames(style['grid-and-footer'], style['no-sidebar'])}>
              <ProductGrid />
              <div className={[style['paging-footer']]}>
                <ProductPagingControlWrapper />
              </div>
            </div>
          </div>
        </ProductContextProvider>
      </PagingContextProvider>
    </div>
  );
};

export default ProductsPage;
